import { useRef, useEffect } from "react";
import "./PickUpPoint.scss";
import { Modal, Form, Typography, Input } from "antd";
import { AddressAutocomplete } from "../../components";
import { useDispatch, useSelector } from "react-redux";
import { resetPatientError } from "../../features/patient/patientSlice";
import pickUpPointSchema from "../../validations/pickUpPointValidation";
import { createYupValidator } from "../../utils/validationHelper";

export default function PickUpPoint({ title, isOpened, onOk, onCancel }) {
  const { loading, error, success } = useSelector((state) => state?.patient);
  const dispatch = useDispatch();
  const formRef = useRef(null);

  useEffect(() => {
    if (isOpened) {
      if (error && !loading) {
        for (let key in error) {
          formRef?.current?.setFields([
            {
              name: key, // Field name you want to set the error for
              errors: [error[key]], // Assuming your error has a 'message' field
            },
          ]);
        }
      }
    }
  }, [loading, error, isOpened]);

  useEffect(() => {
    if (isOpened) {
      if (success) {
        onCancel();
        formRef.current.resetFields();
        dispatch(resetPatientError());
      }
    }
  }, [success, dispatch, isOpened, onCancel]);

  return (
    <Modal
      className="pickUpPoint-cont"
      width={550}
      open={isOpened}
      title={title}
      centered={true}
      destroyOnHidden={true}
      okText={"Save"}
      cancelButtonProps={{
        className: "pickUpPoint-cont__cancelBtn",
      }}
      okButtonProps={{
        size: "large",
        className: "pickUpPoint-cont__btn",
        htmlType: "submit",
        type: "primary",
        key: "submit",
        loading: loading,
        disabled: error ? Object.keys(error)?.length !== 0 : false,
      }}
      onCancel={() => {
        formRef?.current?.resetFields();
        onCancel();
        if (error) {
          dispatch(resetPatientError());
        }
      }}
      onOk={() => {
        formRef?.current?.submit();
      }}>
      <Form
        className="pickUpPoint-cont__form"
        ref={formRef}
        layout="vertical"
        onFinish={(values) => {
          onOk({
            ...values,
          });
        }}>
        <div className="pickUpPoint-cont__form__formItemWrapper">
          <Form.Item
            className="pickUpPoint-cont__form__formItemWrapper__formItem"
            name="address"
            rules={[
              {
                required: true,
                validator: createYupValidator(pickUpPointSchema, "address"),
              },
            ]}
            label={
              <Typography.Text className="pickUpPoint-cont__form__formItemWrapper__formItem__text">
                Address
              </Typography.Text>
            }
            extra="Start typing to autocomplete City, State, and Zip Code"
            colon={false}>
            <AddressAutocomplete
              placeholder="Address"
              size="large"
              onChange={() => {
                if (error && error["address"]) {
                  dispatch(resetPatientError("address"));
                }
              }}
              onAddressSelect={({ street, city, state, zipcode }) => {
                const fullAddress = [street, city, state, zipcode]
                  .filter(Boolean)
                  .join(", ");
                formRef?.current?.setFieldsValue({
                  address: fullAddress,
                  city,
                  state,
                  zipcode,
                });
                ["city", "state", "zipcode"]?.forEach((field) => {
                  if (error && error[field]) {
                    dispatch(resetPatientError(field));
                  }
                });
              }}
            />
          </Form.Item>
        </div>
        <div className="pickUpPoint-cont__form__formItemWrapper">
          <Form.Item
            className="pickUpPoint-cont__form__formItemWrapper__formItem"
            name="city"
            rules={[
              {
                required: true,
                validator: createYupValidator(pickUpPointSchema, "city"),
              },
            ]}
            label={
              <Typography.Text className="pickUpPoint-cont__form__formItemWrapper__formItem__text">
                City
              </Typography.Text>
            }
            colon={false}>
            <Input
              placeholder="City"
              size="large"
              onChange={() => {
                if (error && error["city"]) {
                  dispatch(resetPatientError("city"));
                }
              }}
            />
          </Form.Item>
          <Form.Item
            className="pickUpPoint-cont__form__formItemWrapper__formItem"
            name="state"
            rules={[
              {
                required: true,
                validator: createYupValidator(pickUpPointSchema, "state"),
              },
            ]}
            label={
              <Typography.Text className="pickUpPoint-cont__form__formItemWrapper__formItem__text">
                State
              </Typography.Text>
            }
            colon={false}>
            <Input
              placeholder="State"
              size="large"
              onChange={() => {
                if (error && error["state"]) {
                  dispatch(resetPatientError("state"));
                }
              }}
            />
          </Form.Item>
          <Form.Item
            className="pickUpPoint-cont__form__formItemWrapper__formItem"
            name="zipcode"
            rules={[
              {
                required: true,
                validator: createYupValidator(pickUpPointSchema, "zipcode"),
              },
            ]}
            label={
              <Typography.Text className="pickUpPoint-cont__form__formItemWrapper__formItem__text">
                Zip Code
              </Typography.Text>
            }
            colon={false}>
            <Input
              placeholder="Zip Code"
              size="large"
              onChange={() => {
                if (error && error["zipcode"]) {
                  dispatch(resetPatientError("zipcode"));
                }
              }}
            />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
}
